import dayjs from '@dcp/shared/utils/dayjs'
import {
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@atrvd/saas-ui'
import type { BankingFilters as BankingFiltersType, Facility } from '@/types/banking'
import { safeParseDate } from '@/lib/date-utils'

interface BalanceImportFiltersProps {
  filters: BankingFiltersType
  onFiltersChange: (filters: BankingFiltersType) => void
  facilities: Facility[]
}

/**
 * Special value for "All" option in select dropdowns
 */
const ALL_VALUE = '__all__'

/**
 * Balance Import filters component with date picker and facility dropdown
 * Simplified version of BankingFilters without category and account selectors
 */
export function BalanceImportFilters({
  filters,
  onFiltersChange,
  facilities,
}: BalanceImportFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date Picker */}
      <DatePicker
        date={safeParseDate(filters.endDate)}
        onDateChange={(date) => {
          if (!date) {
            onFiltersChange({ ...filters, endDate: undefined })
            return
          }
          onFiltersChange({ ...filters, endDate: dayjs(date).format('YYYY-MM-DD') })
        }}
        placeholder="Select date"
        className="w-[200px]"
      />

      {/* Facility Select — single-select backed by facilityIds array */}
      <Select
        value={filters.facilityIds?.[0] || ALL_VALUE}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            facilityIds: value === ALL_VALUE ? undefined : [value],
          })
        }
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Facility" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Facilities</SelectItem>
          {facilities.map((facility) => (
            <SelectItem key={facility.id} value={facility.id}>
              {facility.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}